import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Descriptions, Drawer, Spin, Tag } from "antd";
import { fetchUserMonitorList } from "./user-monitor-api";
import UserMonitorCard from "./user-monitor-card";
import {
  CAT_META,
  DEFAULT_CAT_META,
  actionTone,
  formatDate,
  formatTime,
  labelFromMap,
  normalizeListRow,
  stripCatPrefix
} from "./user-monitor-utils";
import "./user-monitor-details-drawer.css";

const DRAWER_WIDTH_KEY = "planOnline.userMonitor.details.width";
const MIN_WIDTH = 480;
const MAX_WIDTH = 960;
const DEFAULT_WIDTH = 640;
const SCREEN_MARGIN = 80;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer с деталями записи журнала и другими событиями по тому же объекту.
 */
const UserMonitorDetailsDrawer = ({ open, record, lookups, onClose, onSelectRecord }) => {
  const [size, setSize] = useState(loadWidth);
  const [related, setRelated] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState("");
  const relatedRequestRef = useRef(0);

  const vidLabel = record ? labelFromMap(lookups?.vids, record.vid, String(record.vid)) : "";
  const catLabel = record
    ? labelFromMap(lookups?.cats, record.idCat, String(record.idCat))
    : "";
  const meta = (record && CAT_META[record.idCat]) || DEFAULT_CAT_META;
  const Icon = meta.icon;
  const tone = record ? actionTone(record.vid, vidLabel) : "neutral";
  const idObjZag = record?.idObjZag ?? 0;

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  useEffect(() => {
    if (!open || !idObjZag) {
      setRelated([]);
      setRelatedError("");
      setRelatedLoading(false);
      return;
    }

    const requestId = ++relatedRequestRef.current;
    setRelatedLoading(true);
    setRelatedError("");

    fetchUserMonitorList({
      idObjZag,
      sortBy: "fdatetime",
      sortDir: "desc",
      take: 50
    })
      .then((data) => {
        if (requestId !== relatedRequestRef.current) return;
        const items = (Array.isArray(data) ? data : data?.items || data?.Items || [])
          .map(normalizeListRow)
          .filter((r) => r && r.idReg != null);
        setRelated(items);
      })
      .catch((e) => {
        if (requestId !== relatedRequestRef.current) return;
        setRelated([]);
        setRelatedError(e.message || "Не удалось загрузить события по объекту");
      })
      .finally(() => {
        if (requestId !== relatedRequestRef.current) return;
        setRelatedLoading(false);
      });
  }, [open, idObjZag]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const items = useMemo(() => {
    if (!record) return [];
    const date = formatDate(record.fdatetime || record.fdate);
    const time = formatTime(record.fdatetime);
    const rows = [
      { key: "id", label: "№ записи", children: `#${record.idReg}` },
      {
        key: "dt",
        label: "Дата и время",
        children: [date, time].filter(Boolean).join(" ")
      },
      { key: "user", label: "Пользователь", children: record.userName || "—" },
      {
        key: "cat",
        label: "Категория",
        children: `${catLabel} (${record.idCat})`
      },
      {
        key: "vid",
        label: "Событие",
        children: `${vidLabel} (${record.vid})`
      }
    ];
    if (record.objZagName || record.idObjZag > 0) {
      rows.push({
        key: "obj",
        label: "Объект",
        children: (
          <>
            {record.objZagName}
            {record.idObjZag > 0 ? (
              <span className="user-monitor-details__obj-id"> [id {record.idObjZag}]</span>
            ) : null}
          </>
        )
      });
    }
    if (record.objCntName || record.idObjCnt > 0) {
      rows.push({
        key: "cnt",
        label: "Уточнение",
        children: (
          <>
            {record.objCntName}
            {record.idObjCnt > 0 ? (
              <span className="user-monitor-details__obj-id"> [id {record.idObjCnt}]</span>
            ) : null}
          </>
        )
      });
    }
    if (record.komment) {
      rows.push({
        key: "komment",
        label: "Комментарий",
        children: <span className="user-monitor-details__komment">{record.komment}</span>
      });
    }
    return rows;
  }, [record, catLabel, vidLabel]);

  const others = related.filter((r) => r.idReg !== record?.idReg);

  return (
    <Drawer
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnClose
      size={size}
      maxSize={maxAllowedWidth()}
      resizable={{
        onResize,
        onResizeEnd
      }}
      className="user-monitor-details"
      styles={{
        body: { padding: "12px 14px" },
        header: { borderLeft: "4px solid transparent" }
      }}
      title={
        record ? (
          <span className="user-monitor-details__title" data-tone={tone} data-cat={record.idCat}>
            <span className="user-monitor-details__icon">
              <Icon />
            </span>
            <span className="user-monitor-details__name">
              {stripCatPrefix(vidLabel) || vidLabel}
            </span>
            {catLabel ? <Tag className="user-monitor-details__cat">{catLabel}</Tag> : null}
          </span>
        ) : null
      }
    >
      {record ? (
        <div className="user-monitor-details__body">
          <Descriptions bordered size="small" column={1} items={items} />

          {idObjZag > 0 ? (
            <div className="user-monitor-details__related">
              <div className="user-monitor-details__related-title">
                Другие события по объекту
              </div>
              {relatedLoading ? (
                <div className="user-monitor-details__center">
                  <Spin size="small" />
                </div>
              ) : relatedError ? (
                <Alert type="error" showIcon message={relatedError} />
              ) : others.length === 0 ? (
                <div className="user-monitor-details__empty">Нет других событий</div>
              ) : (
                <div className="user-monitor-details__related-list">
                  {related.map((r) => (
                    <UserMonitorCard
                      key={r.idReg}
                      record={r}
                      catLabel={labelFromMap(lookups?.cats, r.idCat, String(r.idCat))}
                      vidLabel={labelFromMap(lookups?.vids, r.vid, String(r.vid))}
                      selected={r.idReg === record.idReg}
                      onSelect={() => onSelectRecord?.(r)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
};

export default UserMonitorDetailsDrawer;
